// import FileOutlined from '@haier/icons/FileOutlined';
import LoadingOutlined from '@haier/icons/LoadingOutlined';
import PaperClipOutlined from '@haier/icons/PaperClipOutlined';
import PictureOutlined from '@haier/icons/PictureOutlined';
import classNames from 'classnames';
import CSSMotion, { CSSMotionList } from 'rc-motion';
import * as React from 'react';
import useForceUpdate from '../../_util/hooks/useForceUpdate';
import collapseMotion from '../../_util/motion';
import { cloneElement, isValidElement } from '../../_util/reactNode';
import Button from '../../button';
import { ConfigContext } from '../../config-provider';
import defaultSvg from '../svg/index';
import { isImageUrl, previewImage } from '../utils';
import { getFileByExt } from '../utils/file';
import ListItem from './ListItem';
const listItemMotion = Object.assign({}, collapseMotion);
delete listItemMotion.onAppearEnd;
delete listItemMotion.onEnterEnd;
delete listItemMotion.onLeaveEnd;
const InternalUploadList = (props, ref) => {
    const { listType = 'text', previewFile = previewImage, onPreview, onDownload, onRemove, locale, iconRender, isImageUrl: isImgUrl = isImageUrl, prefixCls: customizePrefixCls, items = [], showPreviewIcon = true, showRemoveIcon = true, showDownloadIcon = false, removeIcon, previewIcon, downloadIcon, progress = { strokeWidth: 2, showInfo: false }, appendAction, appendActionVisible = true, itemRender, } = props;
    const forceUpdate = useForceUpdate();
    const [motionAppear, setMotionAppear] = React.useState(false);
    // ============================= Effect =============================
    React.useEffect(() => {
        if (listType !== 'picture' && listType !== 'picture-card') {
            return;
        }
        (items || []).forEach((file) => {
            if (typeof document === 'undefined' ||
                typeof window === 'undefined' ||
                !window.FileReader ||
                !window.File ||
                !(file.originFileObj instanceof File || file.originFileObj instanceof Blob) ||
                file.thumbUrl !== undefined) {
                return;
            }
            file.thumbUrl = '';
            if (previewFile) {
                previewFile(file.originFileObj).then((previewDataUrl) => {
                    // Need append '' to avoid dead loop
                    file.thumbUrl = previewDataUrl || '';
                    forceUpdate();
                });
            }
        });
    }, [listType, items, previewFile]);
    React.useEffect(() => {
        setMotionAppear(true);
    }, []);
    // ============================= Events =============================
    const onInternalPreview = (file, e) => {
        if (!onPreview) {
            return;
        }
        e === null || e === void 0 ? void 0 : e.preventDefault();
        return onPreview(file);
    };
    const onInternalDownload = (file) => {
        if (typeof onDownload === 'function') {
            onDownload(file);
        }
        else if (file.url) {
            window.open(file.url);
        }
    };
    const onInternalClose = (file) => {
        onRemove === null || onRemove === void 0 ? void 0 : onRemove(file);
    };
    const internalIconRender = (file) => {
        if (iconRender) {
            return iconRender(file, listType);
        }
        const isLoading = file.status === 'uploading';
        const defaultShowSvg = defaultSvg;
        const fileIcon = isImgUrl && isImgUrl(file) ? (React.createElement(PictureOutlined, null)) : (React.createElement("div", { className: `${prefixCls}-list-item-card-ext` },
            React.createElement("div", { className: `${prefixCls}-list-item-card-ext-show` }, React.createElement(defaultShowSvg[getFileByExt(file).defaultSvg]))));
        // const fileIcon = isImgUrl && isImgUrl(file) ? <PictureOutlined /> : <FileOutlined />;
        let icon = isLoading ? React.createElement(LoadingOutlined, null) : React.createElement(PaperClipOutlined, null);
        if (listType === 'picture') {
            icon = isLoading ? React.createElement(LoadingOutlined, null) : fileIcon;
        }
        else if (listType === 'picture-card') {
            icon = isLoading ? locale.uploading : fileIcon;
        }
        return icon;
    };
    const actionIconRender = (customIcon, callback, prefixCls, title) => {
        const btnProps = {
            type: 'text',
            size: 'small',
            title,
            onClick: (e) => {
                callback();
                if (isValidElement(customIcon) && customIcon.props.onClick) {
                    customIcon.props.onClick(e);
                }
            },
            className: `${prefixCls}-list-item-card-actions-btn`,
        };
        if (isValidElement(customIcon)) {
            const btnIcon = cloneElement(customIcon, Object.assign(Object.assign({}, customIcon.props), { onClick: () => { } }));
            return React.createElement(Button, Object.assign({}, btnProps, { icon: btnIcon }));
        }
        return (React.createElement(Button, Object.assign({}, btnProps),
            React.createElement("span", null, customIcon)));
    };
    // ============================== Ref ===============================
    // Test needs
    React.useImperativeHandle(ref, () => ({
        handlePreview: onInternalPreview,
        handleDownload: onInternalDownload,
    }));
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    // ============================= Render =============================
    const prefixCls = getPrefixCls('upload', customizePrefixCls);
    const listClassNames = classNames({
        [`${prefixCls}-list`]: true,
        [`${prefixCls}-list-${listType}`]: true,
        [`${prefixCls}-list-rtl`]: direction === 'rtl',
    });
    // >>> Motion config
    const motionKeyList = [
        ...items.map((file) => ({
            key: file.uid,
            file,
        })),
    ];
    const animationDirection = listType === 'picture-card' ? 'animate-inline' : 'animate';
    // const transitionName = list.length === 0 ? '' : `${prefixCls}-${animationDirection}`;
    let motionConfig = {
        motionDeadline: 2000,
        motionName: `${prefixCls}-${animationDirection}`,
        keys: motionKeyList,
        motionAppear,
    };
    if (listType !== 'picture-card') {
        motionConfig = Object.assign(Object.assign({}, listItemMotion), motionConfig);
    }
    return (React.createElement("div", { className: listClassNames },
        React.createElement(CSSMotionList, Object.assign({}, motionConfig, { component: false }), ({ key, file, className: motionClassName, style: motionStyle }) => (React.createElement(ListItem, { key: key, locale: locale, prefixCls: prefixCls, className: motionClassName, style: motionStyle, file: file, items: items, progress: progress, listType: listType, isImgUrl: isImgUrl, showPreviewIcon: showPreviewIcon, showRemoveIcon: showRemoveIcon, showDownloadIcon: showDownloadIcon, removeIcon: removeIcon, previewIcon: previewIcon, downloadIcon: downloadIcon, iconRender: internalIconRender, actionIconRender: actionIconRender, itemRender: itemRender, onPreview: onInternalPreview, onDownload: onInternalDownload, onClose: onInternalClose }))),
        appendAction && (React.createElement(CSSMotion, Object.assign({}, motionConfig, { visible: appendActionVisible, forceRender: true }), ({ className: motionClassName, style: motionStyle }) => cloneElement(appendAction, (oriProps) => ({
            className: classNames(oriProps.className, motionClassName),
            style: Object.assign(Object.assign(Object.assign({}, motionStyle), { 
                // prevent the element has hover css pseudo-class that may cause animation to end prematurely.
                pointerEvents: motionClassName ? 'none' : undefined }), oriProps.style),
        }))))));
};
const UploadList = React.forwardRef(InternalUploadList);
if (process.env.NODE_ENV !== 'production') {
    UploadList.displayName = 'UploadList';
}
export default UploadList;
